import React, { useState, useMemo, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ScrollReveal from "../components/ui/ScrollReveal";
import { provinces, branchServices } from "../data/mockData";
import { branchesAPI } from "../services/api";
import {
  Search,
  MapPin,
  Phone,
  Mail,
  Clock,
  ChevronRight,
  Map,
  List,
  Filter,
  X,
  Navigation,
  Building2,
  Wallet,
  Car,
  Coins,
  User,
  Briefcase,
  Star,
  Loader2,
} from "lucide-react";

const BranchesPage = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'map'
  const [activeLetter, setActiveLetter] = useState("");

  // Subscribe to real-time branch updates from Firebase
  useEffect(() => {
    let unsubscribe;

    const loadBranches = () => {
      setLoading(true);
      try {
        unsubscribe = branchesAPI.subscribeToBranches(
          (firebaseBranches) => {
            // Only use Firebase branches - no mock data fallback
            // Real-time sync ensures newly added branches appear immediately
            setBranches(firebaseBranches || []);
            setLoading(false);
            console.log(
              "📡 Public page - Branches loaded:",
              firebaseBranches?.length || 0,
            );
          },
          (error) => {
            console.error("Error loading branches:", error);
            // No mock data fallback - show empty state with error
            setBranches([]);
            setLoading(false);
          },
        );
      } catch (error) {
        console.error("Error:", error);
        setBranches([]);
        setLoading(false);
      }
    };

    loadBranches();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Get all unique first letters from branch names
  const alphabetLetters = useMemo(() => {
    const letters = new Set(
      branches.map((b) => b.name?.charAt(0).toUpperCase()).filter(Boolean),
    );
    return Array.from(letters).sort();
  }, [branches]);

  // Filter and sort branches (descending by name)
  const filteredBranches = useMemo(() => {
    return branches
      .filter((branch) => {
        const matchesSearch =
          branch.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          branch.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          branch.district?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesProvince =
          !selectedProvince || branch.province === selectedProvince;

        const matchesService =
          !selectedService ||
          branch.services?.some((s) =>
            s.toLowerCase().includes(selectedService.toLowerCase()),
          );

        const matchesLetter =
          !activeLetter ||
          branch.name?.charAt(0).toUpperCase() === activeLetter;

        return (
          matchesSearch && matchesProvince && matchesService && matchesLetter
        );
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")); // Ascending order by name (A-Z)
  }, [branches, searchQuery, selectedProvince, selectedService, activeLetter]);

  // Map Initialization and Marker Updates
  useEffect(() => {
    if (viewMode === "map" && !loading && mapRef.current) {
      // Safety check for Leaflet availability
      if (!window.L) {
        console.warn("⚠️ Leaflet (L) is not yet available on window");
        return;
      }

      try {
        // Initialize map if not already done
        if (!mapInstance.current) {
          mapInstance.current = window.L.map(mapRef.current).setView(
            [7.8731, 80.7718],
            7,
          );
          window.L.tileLayer(
            "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            {
              attribution: "&copy; OpenStreetMap contributors",
            },
          ).addTo(mapInstance.current);
        }

        // Clear existing markers
        markersRef.current.forEach((marker) => {
          try {
            marker.remove();
          } catch (e) {
            /* ignore */
          }
        });
        markersRef.current = [];

        // Add new markers
        if (filteredBranches.length > 0) {
          const markers = filteredBranches
            .filter(
              (b) =>
                b.coordinates &&
                typeof b.coordinates.lat === "number" &&
                typeof b.coordinates.lng === "number",
            )
            .map((branch) => {
              try {
                const marker = window.L.marker([
                  branch.coordinates.lat,
                  branch.coordinates.lng,
                ]).addTo(mapInstance.current).bindPopup(`
                                        <div style="font-family: Arial, sans-serif; padding: 5px;">
                                            <strong style="color: #0d9488; font-size: 14px;">${branch.name}</strong><br/>
                                            <span style="color: #666; font-size: 12px;">${branch.address}</span><br/>
                                            <div style="margin-top: 5px;">
                                                <a href="https://www.google.com/maps?q=${branch.coordinates.lat},${branch.coordinates.lng}" 
                                                   target="_blank" 
                                                   style="color: #0d9488; text-decoration: none; font-weight: bold; font-size: 11px;">
                                                   Get Directions →
                                                </a>
                                            </div>
                                        </div>
                                    `);
                return marker;
              } catch (e) {
                console.error("Error adding marker:", e);
                return null;
              }
            })
            .filter(Boolean);

          markersRef.current = markers;

          // Adjust view to fit markers if there are any
          if (markers.length > 0) {
            const group = new window.L.featureGroup(markers);
            mapInstance.current.fitBounds(group.getBounds().pad(0.1));
          }
        }
      } catch (err) {
        console.error("❌ Map initialization/update error:", err);
      }
    }

    // Cleanup function
    return () => {
      // Map instance persists across view switches to preserve state,
      // but we could destroy it if memory became an issue
    };
  }, [viewMode, loading, filteredBranches]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedProvince("");
    setSelectedService("");
    setActiveLetter("");
  };

  const hasActiveFilters =
    searchQuery || selectedProvince || selectedService || activeLetter;

  // Service icon mapping
  const getServiceIcon = (service) => {
    const iconMap = {
      ATM: Wallet,
      CRM: Building2,
      "Gold Loan": Coins,
      Leasing: Car,
      "Personal Loan": User,
      "Business Loan": Briefcase,
    };
    return iconMap[service] || Building2;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>
          Branch Network | Find Pride Path Locations Across Sri Lanka
        </title>
        <meta
          name="description"
          content="Find your nearest Pride Path Commercial branch. Island-wide network with ATM, CRM, Gold Loan, Leasing, and Personal Loan services."
        />
        <meta
          property="og:title"
          content="Pride Path Commercial - Branch Network"
        />
        <meta
          property="og:description"
          content="Find your nearest Pride Path branch across Sri Lanka."
        />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="relative pt-[100px] sm:pt-[120px] pb-8 sm:pb-16 bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-4 sm:mb-6 animate-fade-in">
              <Building2 className="w-4 h-4 mr-2" />
              <span>Our Network</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 animate-slide-up">
              Find Your Nearest Branch
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto animate-slide-up-delay">
              With over{" "}
              <span className="font-bold text-yellow-300">
                {branches.length}
              </span>{" "}
              branches across Sri Lanka, we're always close to serve you better.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-4 sm:mb-8 animate-slide-up-delay-2">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by branch name, district, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 sm:py-4 rounded-xl bg-white shadow-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Stats - Real-time count */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-white/90 animate-fade-in-delay">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <Building2 className="w-5 h-5 text-yellow-300 animate-pulse-slow" />
              <span className="font-medium">
                <span className="text-yellow-300 font-bold">
                  {branches.length}
                </span>{" "}
                Branches
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <MapPin className="w-5 h-5 text-yellow-300 animate-bounce-slow" />
              <span className="font-medium">
                {new Set(branches.map((b) => b.province)).size} Provinces
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <Star className="w-5 h-5 text-yellow-300 animate-spin-slow" />
              <span className="font-medium">Island-wide Coverage</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Filters Section */}
      <section className="sticky top-0 bg-white shadow-lg z-40 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3 sm:py-4">
            {/* Filter Dropdowns */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Province Filter */}
              <select
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 hover:bg-gray-100"
              >
                <option value="">All Provinces</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>

              {/* Service Filter */}
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 hover:bg-gray-100"
              >
                <option value="">All Services</option>
                {branchServices.map((service) => (
                  <option key={service.id} value={service.label}>
                    {service.label}
                  </option>
                ))}
              </select>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center justify-center gap-2 min-h-[44px] px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>

            {/* View Toggle & Results Count */}
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
              <span className="text-sm text-gray-500">
                <span className="font-bold text-teal-600">
                  {filteredBranches.length}
                </span>{" "}
                of {branches.length} branches
              </span>
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1.5 min-h-[44px] px-3 sm:px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-teal-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`flex items-center gap-1.5 min-h-[44px] px-3 sm:px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    viewMode === "map"
                      ? "bg-teal-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Map className="w-4 h-4" />
                  <span className="hidden sm:inline">Map</span>
                </button>
              </div>
            </div>
          </div>

          {/* Alphabetical Filter Navigation */}
          <div className="pb-3 -mx-4 px-4 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1 min-w-max">
              <button
                onClick={() => setActiveLetter("")}
                className={`min-w-[40px] h-10 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  !activeLetter
                    ? "bg-teal-600 text-white shadow-lg"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              {alphabetLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() =>
                    setActiveLetter(letter === activeLetter ? "" : letter)
                  }
                  className={`min-w-[40px] h-10 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    letter === activeLetter
                      ? "bg-teal-600 text-white shadow-lg transform scale-110"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="flex-1 py-6 sm:py-10">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-teal-600 animate-spin mb-4" />
              <p className="text-gray-500">Loading branches...</p>
            </div>
          ) : viewMode === "map" ? (
            /* Map View - Interactive map with branch locations */
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Map Container */}
              <div ref={mapRef} className="h-[500px] sm:h-[600px] z-0">
                {/* Leaflet Map will be initialized here */}
              </div>

              {/* Branch List below map */}
              <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border-t">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  {filteredBranches.length} Branch Locations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredBranches.map((branch) => (
                    <a
                      key={branch.id}
                      href={`https://www.google.com/maps?q=${branch.coordinates?.lat || 0},${branch.coordinates?.lng || 0}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-teal-300 group"
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${branch.isHeadOffice ? "bg-gradient-to-br from-amber-500 to-orange-500" : "bg-gradient-to-br from-teal-500 to-teal-600"}`}
                      >
                        {branch.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate group-hover:text-teal-600 transition-colors">
                          {branch.name}
                          {branch.isHeadOffice && (
                            <span className="ml-1 text-xs text-amber-600">
                              ★
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {branch.district}, {branch.province}
                        </p>
                      </div>
                      <Navigation className="w-4 h-4 text-gray-400 group-hover:text-teal-500 transition-colors flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* List View - 3 per row, no letter grouping */
            <div>
              {/* Branch Cards Grid - 3 per row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {filteredBranches.map((branch, branchIndex) => (
                  <div
                    key={branch.id}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-teal-200 transform hover:-translate-y-2 h-full flex flex-col animate-scale-in"
                    style={{ animationDelay: `${branchIndex * 0.03}s` }}
                  >
                    {/* Card Header */}
                    <div className="p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-md group-hover:shadow-lg transform group-hover:scale-110 transition-all duration-300">
                            {branch.name?.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800 group-hover:text-teal-600 transition-colors duration-300">
                              {branch.name}
                            </h3>
                            <span className="text-xs text-gray-400 font-medium">
                              Code: {branch.code}
                            </span>
                          </div>
                        </div>
                        {branch.isHeadOffice && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-sm animate-pulse-slow">
                            <Star className="w-3 h-3 mr-1" />
                            HQ
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-4 sm:p-5 space-y-3 flex-1">
                      {/* Address */}
                      <div className="flex items-start gap-3 group/item">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0 group-hover/item:bg-teal-100 group-hover/item:scale-110 transition-all duration-300">
                          <MapPin className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {branch.address}
                          </p>
                          <p className="text-xs text-teal-600 font-medium mt-1">
                            {branch.district}, {branch.province}
                          </p>
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="flex items-start gap-3 group/item">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover/item:bg-green-100 group-hover/item:scale-110 transition-all duration-300">
                          <Phone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          {(Array.isArray(branch.phone)
                            ? branch.phone
                            : [branch.phone]
                          )
                            .filter(Boolean)
                            .map((phone, idx) => (
                              <a
                                key={idx}
                                href={`tel:${phone?.replace(/\s/g, "")}`}
                                className="block text-sm text-gray-700 hover:text-teal-600 transition-colors duration-200"
                              >
                                {phone}
                              </a>
                            ))}
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start gap-3 group/item">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 group-hover/item:bg-purple-100 group-hover/item:scale-110 transition-all duration-300">
                          <Mail className="w-4 h-4 text-purple-600" />
                        </div>
                        <a
                          href={`mailto:${branch.email}`}
                          className="text-sm text-gray-700 hover:text-teal-600 transition-colors duration-200 truncate"
                        >
                          {branch.email}
                        </a>
                      </div>

                      {/* Hours */}
                      <div className="flex items-start gap-3 group/item">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover/item:bg-blue-100 group-hover/item:scale-110 transition-all duration-300">
                          <Clock className="w-4 h-4 text-blue-600" />
                        </div>
                        <p className="text-sm text-gray-700">{branch.hours}</p>
                      </div>

                      {/* Services */}
                      <div className="pt-2">
                        <div className="flex flex-wrap gap-1.5">
                          {branch.services?.slice(0, 4).map((service, idx) => {
                            const IconComponent = getServiceIcon(service);
                            return (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                              >
                                <IconComponent className="w-3 h-3" />
                                {service}
                              </span>
                            );
                          })}
                          {branch.services?.length > 4 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              +{branch.services.length - 4}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer - Get Directions */}
                    <div className="px-4 sm:px-5 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100 mt-auto">
                      <a
                        href={`https://www.google.com/maps?saddr=My+Location&daddr=${branch.coordinates?.lat || 0},${branch.coordinates?.lng || 0}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] group/btn"
                      >
                        <Navigation className="w-4 h-4 group-hover/btn:animate-bounce" />
                        Get Directions
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* No Results */}
              {filteredBranches.length === 0 && (
                <div className="text-center py-16 animate-fade-in">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    No branches found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Try adjusting your search or filters
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors transform hover:scale-105 duration-200"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 animate-slide-up">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-white/80 mb-6 sm:mb-8 animate-slide-up-delay">
            Our customer service team is here to help you with any inquiries.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-scale-in">
            <a
              href="tel:+94112345678"
              className="flex items-center gap-2 px-6 py-3 bg-white text-teal-700 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Phone className="w-5 h-5" />
              Call Us Now
            </a>
            <a
              href="/contact"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Custom CSS for Animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes scale-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                @keyframes pulse-slow {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-3px); }
                }
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
                .animate-fade-in-delay { animation: fade-in 0.5s ease-out 0.3s forwards; opacity: 0; }
                .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
                .animate-slide-up-delay { animation: slide-up 0.6s ease-out 0.2s forwards; opacity: 0; transform: translateY(20px); }
                .animate-slide-up-delay-2 { animation: slide-up 0.6s ease-out 0.4s forwards; opacity: 0; transform: translateY(20px); }
                .animate-scale-in { animation: scale-in 0.5s ease-out forwards; }
                .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
                .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
                .animate-spin-slow { animation: spin-slow 8s linear infinite; }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `,
        }}
      />
    </div>
  );
};

export default BranchesPage;
